import {
  ActionIcon,
  Badge,
  Chip,
  Group,
  MultiSelect,
  SegmentedControl,
  Stack,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import type { useFilters } from "../hooks/useFilters";

type Filters = ReturnType<typeof useFilters>;

const CHANGE_TYPE_OPTIONS = [
  { value: "price_decrease", label: "Decrease" },
  { value: "price_increase", label: "Increase" },
  { value: "promo_start", label: "Promo" },
  { value: "back_in_stock", label: "Back in Stock" },
  { value: "out_of_stock", label: "Out of Stock" },
  { value: "initial_observation", label: "New Listing" },
];

const DATE_RANGE_OPTIONS = [
  { value: "7", label: "7d" },
  { value: "30", label: "30d" },
  { value: "90", label: "90d" },
  { value: "365", label: "1y" },
];

interface FilterPanelProps {
  filters: Filters;
  showChangeTypes?: boolean;
  showDateRange?: boolean;
}

export function FilterPanel({
  filters,
  showChangeTypes = true,
  showDateRange = true,
}: FilterPanelProps) {
  return (
    <Stack gap="sm">
      <Group gap="sm" wrap="wrap">
        <MultiSelect
          placeholder="Filter brands"
          data={filters.brandOptions.map((b) => ({
            value: b.value,
            label: b.label,
          }))}
          value={filters.filters.brandIds}
          onChange={filters.setBrands}
          clearable
          searchable
          size="sm"
          style={{ minWidth: 180 }}
        />

        <MultiSelect
          placeholder="Filter retailers"
          data={filters.retailerOptions.map((r) => ({
            value: r.value,
            label: r.label,
          }))}
          value={filters.filters.retailerIds}
          onChange={filters.setRetailers}
          clearable
          searchable
          size="sm"
          style={{ minWidth: 180 }}
        />

        {showDateRange && (
          <SegmentedControl
            value={String(filters.filters.days)}
            onChange={(val) => filters.setDays(parseInt(val, 10))}
            data={DATE_RANGE_OPTIONS}
            size="xs"
          />
        )}

        {showChangeTypes && (
          <Chip.Group
            multiple
            value={filters.filters.changeTypes}
            onChange={filters.setChangeTypes}
          >
            <Group gap={4}>
              {CHANGE_TYPE_OPTIONS.map((opt) => (
                <Chip key={opt.value} value={opt.value} size="xs">
                  {opt.label}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
        )}
      </Group>

      {/* Active filter chips */}
      {filters.hasActiveFilters && (
        <FilterChips filters={filters} />
      )}
    </Stack>
  );
}

function FilterChips({ filters }: { filters: Filters }) {
  const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];

  for (const id of filters.filters.brandIds) {
    const opt = filters.brandOptions.find((b) => b.value === id);
    if (opt) {
      chips.push({
        key: `brand-${id}`,
        label: opt.label,
        onRemove: () =>
          filters.setBrands(filters.filters.brandIds.filter((b) => b !== id)),
      });
    }
  }

  for (const id of filters.filters.retailerIds) {
    const opt = filters.retailerOptions.find((r) => r.value === id);
    if (opt) {
      chips.push({
        key: `retailer-${id}`,
        label: opt.label,
        onRemove: () =>
          filters.setRetailers(
            filters.filters.retailerIds.filter((r) => r !== id),
          ),
      });
    }
  }

  for (const ct of filters.filters.changeTypes) {
    const opt = CHANGE_TYPE_OPTIONS.find((o) => o.value === ct);
    if (opt) {
      chips.push({
        key: `change-${ct}`,
        label: opt.label,
        onRemove: () =>
          filters.setChangeTypes(
            filters.filters.changeTypes.filter((c) => c !== ct),
          ),
      });
    }
  }

  if (chips.length === 0) return null;

  return (
    <Group gap={6}>
      {chips.map((chip) => (
        <Badge
          key={chip.key}
          variant="light"
          rightSection={
            <ActionIcon
              size="xs"
              variant="transparent"
              onClick={chip.onRemove}
              aria-label={`Remove ${chip.label} filter`}
            >
              <IconX size={10} />
            </ActionIcon>
          }
        >
          {chip.label}
        </Badge>
      ))}
      <Badge
        variant="subtle"
        color="gray"
        style={{ cursor: "pointer" }}
        onClick={filters.clearAll}
      >
        Clear all
      </Badge>
    </Group>
  );
}
